import Vue from 'vue'

/**
 * Membuat instance Vue untuk menangani pengambilan data secara paginasi (bertahap).
 *
 * Fungsi ini mengembalikan objek Vue yang memiliki state dan method `fetchData`
 * untuk melakukan pemanggilan data menggunakan fungsi `fetchFn`.
 *
 * @param {(params: { offset: number, limit: number, [key: string]: any }) => Promise<{ data: any[] }>} fetchFn
 *   Fungsi async yang digunakan untuk mengambil data, menerima `offset`, `limit`, dan parameter opsional lainnya.
 * @param {number} [defaultOffset=0] - Offset awal untuk paginasi.
 * @param {number} [defaultLimit=10] - Jumlah data per halaman yang diambil.
 *
 * @returns {Vue} Objek Vue dengan data reaktif dan method `fetchData` untuk mengambil data secara paginasi.
 *
 * @example
 * const fetcher = createPaginatedFetcher(api.getUsers)
 * fetcher.fetchData()
 *
 * @example
 * // Reset data dan ambil ulang dari awal
 * fetcher.fetchData({ reset: true })
 *
 * @example
 * // Ambil data dengan opsi tambahan
 * fetcher.fetchData({ options: { search: 'abc' } })
 */
// eslint-disable-next-line import/prefer-default-export
export function createPaginatedFetcher(fetchFn, defaultOffset = 0, defaultLimit = 50) {
  const fetcher = Vue.observable({
    data: [],
    offset: defaultOffset,
    limit: defaultLimit,
    isLoading: false,
    error: null,
    hasMore: false,
    options: {},
  })

  fetcher.fetchData = async ({ reset = false, options = {} } = {}) => {
    if (reset) {
      fetcher.offset = defaultOffset
      fetcher.data = []
      fetcher.hasMore = false
      fetcher.options = options
    }

    if (fetcher.isLoading || (!fetcher.hasMore && fetcher.offset > defaultOffset)) return

    fetcher.isLoading = true
    fetcher.error = null

    try {
      const result = await fetchFn({ offset: fetcher.offset, limit: fetcher.limit, ...fetcher.options })
      const items = result?.data || []

      fetcher.data.push(...items)
      fetcher.offset += items.length
      fetcher.hasMore = items.length >= fetcher.limit
    } catch (err) {
      fetcher.error = err
    } finally {
      fetcher.isLoading = false
    }
  }

  return fetcher
}
